import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Upload, Trash2, Image, AlertCircle, Link, Plus, ExternalLink, Monitor, Smartphone, Move, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Position options for image focal point
const POSITION_OPTIONS = [
  { value: 'center', label: 'Center' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'top left', label: 'Top Left' },
  { value: 'top right', label: 'Top Right' },
  { value: 'bottom left', label: 'Bottom Left' },
  { value: 'bottom right', label: 'Bottom Right' },
];

const AdminHomepageImages = () => {
  const { adminAuth } = useOutletContext();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [mobileUrl, setMobileUrl] = useState('');
  const [newDeviceType, setNewDeviceType] = useState('both'); // 'both', 'desktop', 'mobile'
  const [addingUrl, setAddingUrl] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  // Track device view per image (key: image.id, value: 'desktop' or 'mobile')
  const [imageDeviceViews, setImageDeviceViews] = useState({});
  
  const getDeviceView = (imageId) => imageDeviceViews[imageId] || 'desktop';
  const setDeviceView = (imageId, view) => setImageDeviceViews(prev => ({ ...prev, [imageId]: view }));

  const fetchImages = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/homepage-images`);
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }
    } catch (error) {
      toast.error('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleFileUpload = async (e, isMobile = false) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('is_mobile', isMobile.toString());

      try {
        const response = await fetch(`${API_URL}/api/admin/upload/homepage`, {
          method: 'POST',
          headers: {
            'Authorization': `Basic ${adminAuth}`
          },
          body: formData
        });

        if (response.ok) {
          successCount++;
        } else {
          const error = await response.json();
          toast.error(`Failed to upload ${file.name}: ${error.detail}`);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} image(s) uploaded successfully`);
      fetchImages();
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleAddExternalUrl = async () => {
    if (!externalUrl.trim()) {
      toast.error('Please enter a desktop image URL');
      return;
    }

    if (!externalUrl.startsWith('http://') && !externalUrl.startsWith('https://')) {
      toast.error('URL must start with http:// or https://');
      return;
    }

    setAddingUrl(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/homepage-images/url`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          url: externalUrl.trim(),
          mobile_url: mobileUrl.trim() || null,
          position: 'center',
          device_type: newDeviceType
        })
      });

      if (response.ok) {
        toast.success('Image added successfully');
        setExternalUrl('');
        setMobileUrl('');
        setNewDeviceType('both');
        setShowUrlInput(false);
        fetchImages();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add image');
      }
    } catch (error) {
      toast.error('Connection error');
    }
    setAddingUrl(false);
  };

  const handleUpdatePosition = async (imageId, position) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/homepage-images/${imageId}/position`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ position })
      });

      if (response.ok) {
        toast.success('Position updated');
        fetchImages();
        setEditingImage(null);
      } else {
        toast.error('Failed to update position');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleUpdateMobileUrl = async (imageId, newMobileUrl) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/homepage-images/${imageId}/mobile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mobile_url: newMobileUrl || null })
      });

      if (response.ok) {
        toast.success('Mobile image updated');
        fetchImages();
      } else {
        toast.error('Failed to update mobile image');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleUpdateMobilePosition = async (imageId, position) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/homepage-images/${imageId}/mobile-position`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mobile_position: position })
      });

      if (response.ok) {
        toast.success('Mobile position updated');
        fetchImages();
      } else {
        toast.error('Failed to update mobile position');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/upload/homepage/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${adminAuth}`
        }
      });

      if (response.ok) {
        toast.success('Image deleted');
        fetchImages();
      } else {
        toast.error('Failed to delete image');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleUpdateDeviceType = async (imageId, deviceType) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/homepage-images/${imageId}/device-type`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ device_type: deviceType })
      });

      if (response.ok) {
        toast.success(`Image will show on: ${deviceType === 'both' ? 'All devices' : deviceType}`);
        fetchImages();
      } else {
        toast.error('Failed to update device type');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const isExternalUrl = (url) => url?.startsWith('http');

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Homepage Images</h1>
          <p className="text-gray-600">Manage hero images with desktop & mobile versions</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="border-amber-600 text-amber-600 hover:bg-amber-50"
          >
            <Link className="w-4 h-4 mr-2" />
            Add URL
          </Button>
          <div className="relative">
            <input
              type="file"
              id="image-upload"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e, false)}
              className="hidden"
              disabled={uploading}
            />
            <label htmlFor="image-upload">
              <Button 
                asChild
                className="bg-amber-600 hover:bg-amber-700 cursor-pointer"
                disabled={uploading}
              >
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </span>
              </Button>
            </label>
          </div>
        </div>
      </div>

      {/* External URL Input with Mobile Option */}
      {showUrlInput && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <Monitor className="w-4 h-4" /> Image URL *
              </label>
              <Input
                type="url"
                placeholder="https://images.unsplash.com/photo-..."
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                className="bg-white"
              />
            </div>
            
            {/* Device Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Show this image on:
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNewDeviceType('both')}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    newDeviceType === 'both' 
                      ? 'bg-amber-600 text-white border-amber-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Monitor className="w-4 h-4" />+<Smartphone className="w-4 h-4" /> Both
                </button>
                <button
                  type="button"
                  onClick={() => setNewDeviceType('desktop')}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    newDeviceType === 'desktop' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Monitor className="w-4 h-4" /> Desktop Only
                </button>
                <button
                  type="button"
                  onClick={() => setNewDeviceType('mobile')}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    newDeviceType === 'mobile' 
                      ? 'bg-green-600 text-white border-green-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Smartphone className="w-4 h-4" /> Mobile Only
                </button>
              </div>
              <p className="text-xs text-amber-700 mt-2">
                {newDeviceType === 'both' && '⚡ Image will appear on all devices'}
                {newDeviceType === 'desktop' && '🖥️ Image will ONLY appear on desktop/laptop'}
                {newDeviceType === 'mobile' && '📱 Image will ONLY appear on mobile/tablet'}
              </p>
            </div>

            {/* Mobile URL only relevant if device_type includes mobile */}
            {(newDeviceType === 'both' || newDeviceType === 'mobile') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" /> Mobile-specific Image URL (optional)
                </label>
                <Input
                  type="url"
                  placeholder="https://images.unsplash.com/photo-... (portrait/square recommended)"
                  value={mobileUrl}
                  onChange={(e) => setMobileUrl(e.target.value)}
                  className="bg-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional: Different image optimized for mobile screens
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowUrlInput(false); setNewDeviceType('both'); }}>Cancel</Button>
              <Button
                onClick={handleAddExternalUrl}
                disabled={addingUrl || !externalUrl.trim()}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {addingUrl ? 'Adding...' : <><Plus className="w-4 h-4 mr-1" /> Add Image</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm">
            <p className="text-blue-800 font-medium">Tips for best results:</p>
            <ul className="text-blue-700 mt-1 space-y-1">
              <li>• <strong>Desktop:</strong> Use landscape images (1920x1080 or wider)</li>
              <li>• <strong>Mobile:</strong> Use portrait/square images (1080x1920 or 1:1)</li>
              <li>• <strong>Position:</strong> Adjust focal point if subject gets cropped</li>
              <li>• External URLs (Unsplash, Cloudinary) persist after redeployment</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Images Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {images.map((image, index) => {
          const deviceView = getDeviceView(image.id);
          return (
          <Card key={image.id} className="overflow-hidden">
            {/* Preview Tabs - Per Image */}
            <div className="flex border-b">
              <button
                onClick={() => setDeviceView(image.id, 'desktop')}
                className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1 ${deviceView === 'desktop' ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' : 'text-gray-500'}`}
              >
                <Monitor className="w-4 h-4" /> Desktop
              </button>
              <button
                onClick={() => setDeviceView(image.id, 'mobile')}
                className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-1 ${deviceView === 'mobile' ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' : 'text-gray-500'}`}
              >
                <Smartphone className="w-4 h-4" /> Mobile
              </button>
            </div>

            {/* Image Preview */}
            <div className="relative group">
              {deviceView === 'desktop' ? (
                <div className="h-40 bg-gray-100">
                  <img
                    src={getFullImageUrl(image.url)}
                    alt={`Homepage ${index + 1}`}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: image.position || 'center' }}
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found'; }}
                  />
                </div>
              ) : (
                <div className="h-40 bg-gray-100 flex justify-center">
                  <div className="w-24 h-full bg-gray-200 overflow-hidden rounded-lg shadow-inner">
                    <img
                      src={getFullImageUrl(image.mobile_url || image.url)}
                      alt={`Mobile ${index + 1}`}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: image.mobile_position || image.position || 'center' }}
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/300x600?text=Mobile'; }}
                    />
                  </div>
                </div>
              )}
              
              {/* Overlay badges */}
              <div className="absolute top-2 left-2 flex gap-1">
                <span className="bg-black/60 text-white text-xs px-2 py-1 rounded">#{index + 1}</span>
                {isExternalUrl(image.url) && (
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                  </span>
                )}
                {/* Device type badge */}
                <span className={`text-white text-xs px-2 py-1 rounded flex items-center gap-1 ${
                  image.device_type === 'desktop' ? 'bg-blue-500' :
                  image.device_type === 'mobile' ? 'bg-green-600' :
                  'bg-amber-500'
                }`}>
                  {image.device_type === 'desktop' && <><Monitor className="w-3 h-3" /> Desktop</>}
                  {image.device_type === 'mobile' && <><Smartphone className="w-3 h-3" /> Mobile</>}
                  {(!image.device_type || image.device_type === 'both') && <>All</>}
                </span>
              </div>

              {/* Delete button */}
              <button
                onClick={() => handleDelete(image.id)}
                className="absolute top-2 right-2 p-1.5 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Controls */}
            <CardContent className="p-3 space-y-3">
              {/* Device Type Selector */}
              <div>
                <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                  <Settings className="w-3 h-3" /> Show on
                </label>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleUpdateDeviceType(image.id, 'both')}
                    className={`flex-1 py-1 px-2 text-xs rounded flex items-center justify-center gap-1 ${
                      (!image.device_type || image.device_type === 'both') 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => handleUpdateDeviceType(image.id, 'desktop')}
                    className={`flex-1 py-1 px-2 text-xs rounded flex items-center justify-center gap-1 ${
                      image.device_type === 'desktop' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Monitor className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleUpdateDeviceType(image.id, 'mobile')}
                    className={`flex-1 py-1 px-2 text-xs rounded flex items-center justify-center gap-1 ${
                      image.device_type === 'mobile' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Smartphone className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Position Control - Show different based on device view */}
              <div>
                <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                  <Move className="w-3 h-3" /> {deviceView === 'desktop' ? 'Desktop' : 'Mobile'} Position
                </label>
                <select
                  value={deviceView === 'desktop' ? (image.position || 'center') : (image.mobile_position || image.position || 'center')}
                  onChange={(e) => {
                    if (deviceView === 'desktop') {
                      handleUpdatePosition(image.id, e.target.value);
                    } else {
                      handleUpdateMobilePosition(image.id, e.target.value);
                    }
                  }}
                  className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
                >
                  {POSITION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Mobile URL */}
              <div>
                <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                  <Smartphone className="w-3 h-3" /> Mobile Image
                </label>
                {editingImage === image.id ? (
                  <div className="flex gap-1">
                    <Input
                      type="url"
                      placeholder="Mobile URL..."
                      defaultValue={image.mobile_url || ''}
                      id={`mobile-url-${image.id}`}
                      className="text-xs h-8"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        const input = document.getElementById(`mobile-url-${image.id}`);
                        handleUpdateMobileUrl(image.id, input.value);
                        setEditingImage(null);
                      }}
                      className="h-8 bg-amber-600 hover:bg-amber-700"
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingImage(image.id)}
                    className="w-full text-left text-xs border rounded-md px-2 py-1.5 bg-gray-50 hover:bg-gray-100 truncate"
                  >
                    {image.mobile_url ? (
                      <span className="text-green-600">✓ Custom mobile image set</span>
                    ) : (
                      <span className="text-gray-400">+ Add mobile-specific image</span>
                    )}
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
          );
        })}

        {images.length === 0 && (
          <div className="col-span-full">
            <Card className="border-dashed border-2">
              <CardContent className="p-12 text-center">
                <Image className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No images uploaded</h3>
                <p className="text-gray-500 mb-4">Add images with separate desktop & mobile versions</p>
                <Button onClick={() => setShowUrlInput(true)} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Image
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Summary */}
      {images.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">
              <strong>{images.length}</strong> image{images.length !== 1 ? 's' : ''} • 
              <strong> {images.filter(img => img.mobile_url).length}</strong> with mobile versions • 
              Images cycle every 5 seconds
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminHomepageImages;
